import api, { ApiResponse, QueryPayload, QueryResponse, WhereCondition } from './api';

export interface Campaign {
  id: string;
  ad_account_id: string;
  fb_campaign_id: string | null;
  name: string;
  objective: string;
  status: string;
  effective_status: string | null;
  status_stale_since: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  special_ad_categories: string[] | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  ad_account?: {
    id: string;
    name: string;
  };
}

export interface CampaignFilters {
  ad_account_id?: string;
  status?: string | string[];
  search?: string;
  offset?: number;
  limit?: number;
}

export interface CreateCampaignRequest {
  ad_account_id: string;
  name: string;
  objective: string;
  status?: string;
  daily_budget?: number;
  lifetime_budget?: number;
  special_ad_categories?: string[];
}

export interface UpdateCampaignRequest {
  name?: string;
  status?: string;
  daily_budget?: number;
  lifetime_budget?: number;
}

export interface QueryResult<T> {
  data: T[];
  total: number;
}

function buildWhereCondition(filters: CampaignFilters): WhereCondition | undefined {
  const conditions: WhereCondition[] = [];

  if (filters.ad_account_id) {
    conditions.push({ campaign: { ad_account_id: filters.ad_account_id } });
  }

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    conditions.push({ campaign: { status: { op: 'in', value: statuses } } });
  }

  if (filters.search) {
    conditions.push({ campaign: { name: { op: 'ilike', value: `%${filters.search}%` } } });
  }

  return conditions.length > 0 ? { $and: conditions } : undefined;
}

export const campaignsService = {
  query: async (filters: CampaignFilters = {}): Promise<QueryResult<Campaign>> => {
    const payload: QueryPayload = {
      where: buildWhereCondition(filters),
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 25,
      order_by: 'campaign.created_at',
      order: 'desc',
    };

    const response = await api.post<QueryResponse<Campaign>>('/campaigns/query', payload);
    return {
      data: response.data.data,
      total: response.data._meta?.total ?? 0,
    };
  },

  getById: async (id: string): Promise<Campaign> => {
    const response = await api.get<ApiResponse<Campaign>>(`/campaigns/${id}`);
    return response.data.data!;
  },

  create: async (data: CreateCampaignRequest): Promise<Campaign> => {
    const response = await api.post<ApiResponse<Campaign>>('/campaigns', data);
    return response.data.data!;
  },

  update: async (id: string, data: UpdateCampaignRequest): Promise<Campaign> => {
    const response = await api.patch<ApiResponse<Campaign>>(`/campaigns/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/campaigns/${id}`);
  },

  duplicate: async (id: string): Promise<Campaign> => {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${id}/duplicate`);
    return response.data.data!;
  },

};
